/**
 * The command log's door — ADR-007 §2/§3: a command is DATA (serializable,
 * zod-gated, identity-addressed), and `applyCommand` is the only
 * Document→Document path within a lineage. `parseDocument` LOADS (begins a
 * lineage); this module EDITS inside one (§4's two doors). Every result
 * re-runs the parseDocument gate, so every structural invariant holds after
 * every command by RE-CHECK, not by trust — and a refusal is atomic: the
 * frozen input is untouched and no partial state can escape. Deterministic by
 * construction: no clock, no RNG, no ambient state (§4; ADR-011 §2.4 is the
 * replay ceiling).
 * Depends on: zod, ir errors, ir rig-descriptor (quat schema), session/document.
 * Depended on by: session/history, the app store, the native/agent register.
 */
import { z } from 'zod';
import { CommandRefusal, SchemaValidationError } from '../ir/errors.js';
import { tailSpringSettingsSchema } from '../springs/spring-drive.js';
import {
  jointRecenterSchema,
  armSpaceDegreesSchema,
  colorFactorSchema,
  headSpaceDegreesSchema,
  keyIdSchema,
  metallicFactorSchema,
  normalScaleSchema,
  zoneIdSchema,
  zoneIntentSchema,
  zoneSchema,
  zoneSourceSchema,
  zoneShapeSchema,
  tailDeclarationSchema,
  softTissueDeclarationSchema,
  hairDeclarationSchema,
  hairGravitySchema,
  hairMotionSchema,
  occlusionStrengthSchema,
  packageChoicesSchema,
  parseDocument,
  poseRotationSchema,
  roughnessFactorSchema,
  type AnimatorDocument,
  type AuthoredClip,
  type KeyTrack,
  type Zone,
} from './document.js';

/**
 * The vocabulary, closed (§3.1): every future surface lands its verbs with
 * its Document slice and its tests. Adding a verb is a schema branch + an
 * interpreter case + tests — NEVER an undo implementation (§2: correct undo
 * for every verb is the substrate's property, not per-verb coverage).
 * The timeline slice (ADR-006 §13.4) lands `clip.*`/`key.*` — unblocked
 * because keys now carry identities (§3.2's law held: no verb landed first).
 */
export const COMMAND_VERBS = [
  'pose.set',
  'pose.clear',
  'clip.create',
  'clip.materialize',
  'key.set',
  'key.delete',
  'key.retime',
  'armSpace.set',
  'headSpace.set',
  'tailSpring.set',
  'material.setFactor',
  'material.setMapUse',
  'material.setColor',
  'zone.create',
  'zone.delete',
  'zone.rename',
  'zone.setFaces',
  'zone.confirm',
  'tail.declare',
  'tail.withdraw',
  'softTissue.declare',
  'softTissue.withdraw',
  'hair.confirm',
  'hair.setGravity',
  'hair.setMotion',
  'hair.withdraw',
  'package.set',
  'elbow.pin',
  'elbow.reset',
] as const;
export type CommandVerb = (typeof COMMAND_VERBS)[number];

const poseSetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('pose.set'),
  /**
   * Upserts the bone's pose-layer value (§3.1 — today's Document semantics;
   * History owns the walk). Rotation is unit-gated at BOTH doors — same
   * schema as the Document's own pose layer (Codex P2, #52).
   */
  params: z.object({ bone: z.string().min(1), rotation: poseRotationSchema }),
});

const poseClearSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('pose.clear'),
  params: z.object({ bone: z.string().min(1) }),
});

const clipCreateSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('clip.create'),
  /** A clip's name is its identity — creating a taken name refuses. */
  params: z.object({ name: z.string().min(1) }),
});

const keyTimeSchema = z.number().finite().nonnegative();

/**
 * A materialized track as it ARRIVES: bone + values over time, no identities.
 * Ids are minted by the interpreter from a fresh counter (§12.2 — identity is
 * the Document's to assign, never the caller's), so replay mints identically.
 */
const materializedTrackSchema = z.object({
  bone: z.string().min(1),
  keys: z.array(z.object({ time: keyTimeSchema, rotation: poseRotationSchema })).min(1),
});

const clipMaterializeSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('clip.materialize'),
  /**
   * An imported clip becoming AUTHORED (ADR-008 §(g): post-retarget the dense
   * output is canonical, and the keyframe schema was written for it — "fps is
   * the DISPLAY declaration, never the storage unit, so imported clips can
   * materialize into this schema without quantizing").
   *
   * The keys travel IN the command rather than being re-derived at replay from
   * a retarget the interpreter would have to run: `applyCommand` is pure over
   * the Document by construction (no clock, no RNG, no ambient state), and
   * re-deriving would make replay depend on the engine, the specimen bytes and
   * the retarget's version all at once. History is byte-bounded and measures
   * entries (§3.3), so a large entry is a thing the substrate accounts for.
   *
   * Rotation-only, like every other key track in v1. A caller with translation
   * or scale to carry must be told what was left behind — that is
   * `materializeClip`'s report, not a silent narrowing here.
   */
  params: z.object({
    name: z.string().min(1),
    tracks: z.array(materializedTrackSchema).min(1),
  }),
});

const keySetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('key.set'),
  /**
   * Autokey's shape (§3.2): at a NEW time this mints an identity from the
   * track's stored counter; at an EXISTING time it replaces that key's value
   * and KEEPS its id (§12.2 — a value edit never re-derives identity). The
   * first key on a bone creates its track; the clip must already exist.
   */
  params: z.object({
    clip: z.string().min(1),
    bone: z.string().min(1),
    time: keyTimeSchema,
    rotation: poseRotationSchema,
  }),
});

const keyDeleteSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('key.delete'),
  params: z.object({ clip: z.string().min(1), bone: z.string().min(1), id: keyIdSchema }),
});

const keyRetimeSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('key.retime'),
  /** The operation v2 had nowhere to put (review §3.2) — addressable at last. */
  params: z.object({ clip: z.string().min(1), bone: z.string().min(1), id: keyIdSchema, time: keyTimeSchema }),
});

const armSpaceSetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('armSpace.set'),
  /**
   * The tier-1 rotate-at-play character property (ADR-017): degrees of
   * constant upper-arm abduction the display layer composes over clips.
   * Band-gated by the SAME schema the Document's own slice wears — two doors,
   * one value. Zero is the neutral state, stored as zero (a slider at rest is
   * a declaration, not an absence); the Document's `armSpace` field being
   * absent is equivalent by contract.
   */
  params: z.object({ degrees: armSpaceDegreesSchema }),
});

/**
 * ADR-038 R3 (ruled 2026-09-24): the tail spring's settings are Document data
 * with ONE verb — an upsert like armSpace.set, so a moved knob or a new mood
 * is undoable, saved, and what the export declares. It changes no armature:
 * the spring plays at display time over whatever chain the run grew.
 */
const tailSpringSetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('tailSpring.set'),
  params: z.object({ settings: tailSpringSettingsSchema }),
});

const headSpaceSetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('headSpace.set'),
  /**
   * The Character panel's second tenant (head-space.ts): degrees of constant
   * Neck+Head pitch (+ = erect) the display layer composes over clips.
   * Band-gated by the SAME schema the Document's own slice wears — two doors,
   * one value. Setting — zero included — is an explicit declaration that
   * replaces the measured seating baseline; only an ABSENT Document field
   * means "use the baseline" (see the edits schema).
   */
  params: z.object({ degrees: headSpaceDegreesSchema }),
});

/** Each scalar channel's band IS its Document schema (two doors, one value). */
const SCALAR_CHANNEL_GATES = {
  metallic: metallicFactorSchema,
  roughness: roughnessFactorSchema,
  normalScale: normalScaleSchema,
  occlusionStrength: occlusionStrengthSchema,
} as const;

const materialSetMapUseSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('material.setMapUse'),
  /**
   * DETACH one channel of the metallicRoughness texture, or reattach it.
   *
   * The factor multiplies the map, which is the wrong instrument when the map
   * itself is wrong: Tripo's newer class paints skin at roughness ~0.04 with
   * metallic up to 0.96, and multiplying to matte needs ~x23 — amplifying every
   * variation in the map 23x before clamping, for a blotchy result that also
   * saturates invisibly (three clamps effective roughness at 1.0). Detaching
   * gives the clean constant the map cannot. World's material panel offers the
   * same per-channel switch; this is the suite saying one thing in two places.
   */
  params: z.object({
    material: z.string().min(1),
    channel: z.enum(['metallic', 'roughness']),
    /** false = ignore the map on this channel and use the factor alone. */
    fromMap: z.boolean(),
  }),
});

const materialSetFactorSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('material.setFactor'),
  /**
   * The Materials panel's scalar commit (ADR-020 T2, §11: export-real): set
   * ONE channel's factor on ONE material, upsert semantics like pose.set.
   * The band is the channel's own Document schema (two doors, one value) —
   * metallic [0,1] (glTF's range), roughness [0,2] (>1 is the wet-skin cure,
   * baked into the texture at export), normal scale [0,2], occlusion
   * strength [0,1]. `material` names the glTF material; whether the live
   * character carries it — or carries the TEXTURE a channel scales (normal
   * scale and occlusion strength are texture attributes) — is asked at apply
   * time (the armSpace posture — this gate cannot know the scene).
   */
  params: z
    .object({
      material: z.string().min(1),
      channel: z.enum(['metallic', 'roughness', 'normalScale', 'occlusionStrength']),
      value: z.number().finite(),
    })
    .superRefine((p, ctx) => {
      const result = SCALAR_CHANNEL_GATES[p.channel].safeParse(p.value);
      for (const issue of result.success ? [] : result.error.issues) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: issue.message });
      }
    }),
});

const materialSetColorSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('material.setColor'),
  /**
   * The color-valued twin: base-color tint (`baseColorFactor`, alpha left
   * alone) and emissive (`emissiveFactor`). Values are LINEAR-space RGB in
   * [0,1]³ — glTF's own shape and space; a hex picker converts sRGB→linear
   * BEFORE this gate, because the Document speaks the file's truth-space.
   */
  params: z.object({
    material: z.string().min(1),
    channel: z.enum(['baseColor', 'emissive']),
    value: colorFactorSchema,
  }),
});

/**
 * ADR-031 zones. A zone is Document data with command verbs — undoable through
 * the log like a pose — because creating, growing or renaming a face set
 * changes no armature. (A tail, which does, rides its own verbs below —
 * `tail.declare`, the `elbow.pin` precedent, ADR-031 §3.3a.)
 */
const zoneCreateSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('zone.create'),
  params: z.object({ zone: zoneSchema }),
});
const zoneDeleteSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('zone.delete'),
  params: z.object({ id: zoneIdSchema }),
});
const zoneRenameSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('zone.rename'),
  params: z.object({ id: zoneIdSchema, name: z.string().min(1) }),
});
const zoneSetFacesSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('zone.setFaces'),
  /** A new face set REPLACES the old, and says where it came from — the provenance grows, never rewrites. */
  params: z.object({
    id: zoneIdSchema,
    faces: z.array(z.number().int().nonnegative()).min(1),
    source: zoneSourceSchema,
    /** The shape the new faces read as, when it changed (a wand-made stub a ➕ grew into a tail, Penny 2026-09-25). */
    shape: zoneShapeSchema.optional(),
  }),
});
const zoneConfirmSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('zone.confirm'),
  /** "This is a tail" / "this is cloth" — a human act, so it carries who (the `human:` namespace). */
  params: z.object({ id: zoneIdSchema, intent: zoneIntentSchema, confirmedBy: z.string().regex(/^human:\S+$/) }),
});

/**
 * ADR-031 §3.3a (T2, ruled 2026-09-22): a tail is DECLARED through the log —
 * the `elbow.pin` precedent, an armature change inside a lineage that the
 * surface re-projects through `runDocument`. Linear undo means no later edit
 * can address a slot an undone tail removed; the one hazard left — a count
 * reduction or a withdrawal stranding a pose or key already on a slot — is
 * refused by name in `applyCommand`. `tail.declare` is an upsert keyed by the
 * zone; the count carries its provenance (`default` measured, `declared` moved).
 */
const tailDeclareSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('tail.declare'),
  params: tailDeclarationSchema,
});
const tailWithdrawSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('tail.withdraw'),
  params: z.object({ zoneId: zoneIdSchema }),
});

/**
 * ADR-038 J2: a soft-tissue leaf is DECLARED through the log — the tail's
 * precedent, an armature change the surface re-projects through
 * `runDocument`. `softTissue.declare` is an upsert keyed by the region (a new
 * zone replaces the old); `softTissue.withdraw` removes the region's leaf.
 * No clip key can address a leaf (they are outside every profile), so there
 * is nothing a withdrawal could strand.
 */
const softTissueDeclareSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('softTissue.declare'),
  params: softTissueDeclarationSchema,
});
const softTissueWithdrawSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('softTissue.withdraw'),
  params: z.object({ region: softTissueDeclarationSchema.shape.region }),
});

/**
 * ADR-039 T1: hair is CONFIRMED through the log, region and skull together.
 * `hair.confirm` writes the hair zone (intent `hair`) and the declaration in
 * ONE command — one undo removes both — and is an upsert: a re-confirm (the
 * skull dragged again) replaces the previous hair zone and record.
 * `hair.setGravity` moves the one setting; `hair.withdraw` ("not hair" after a
 * confirm) removes the record and its zone. No run consumes it in T1 (the
 * weights are computed and reported, not applied — ADR-039 §5), so nothing a
 * withdrawal could strand.
 */
const hairConfirmSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('hair.confirm'),
  params: z.object({ zone: zoneSchema, hair: hairDeclarationSchema }),
});
const hairSetGravitySchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('hair.setGravity'),
  params: z.object({ gravity: hairGravitySchema }),
});
/** ADR-039 §14: the softened hair's Bounce / Sway / Jiggle — one undoable set per slider release. */
const hairSetMotionSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('hair.setMotion'),
  params: z.object({ motion: hairMotionSchema }),
});
const hairWithdrawSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('hair.withdraw'),
  params: z.object({}),
});

/**
 * ADR-033: the author's package choices are Document data with ONE verb — an
 * upsert like armSpace.set, so a changed role or a re-starred take is undoable
 * and the block is never edited outside the log.
 */
const packageSetSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  verb: z.literal('package.set'),
  params: z.object({ package: packageChoicesSchema }),
});

const elbowPinSchema = z.object({
  schemaVersion: z.literal('1.0.0'), verb: z.literal('elbow.pin'),
  params: jointRecenterSchema.extend({ rebuildHinge: z.literal(true), method: z.literal('limb-axis') }),
});
const elbowResetSchema = z.object({
  schemaVersion: z.literal('1.0.0'), verb: z.literal('elbow.reset'),
  params: z.object({ bone: z.string().min(1) }),
});

export const commandSchema = z.discriminatedUnion('verb', [
  elbowPinSchema, elbowResetSchema,
  poseSetSchema,
  poseClearSchema,
  clipCreateSchema,
  clipMaterializeSchema,
  keySetSchema,
  keyDeleteSchema,
  keyRetimeSchema,
  armSpaceSetSchema,
  headSpaceSetSchema,
  tailSpringSetSchema,
  materialSetFactorSchema,
  materialSetMapUseSchema,
  materialSetColorSchema,
  zoneCreateSchema,
  zoneDeleteSchema,
  zoneRenameSchema,
  zoneSetFacesSchema,
  zoneConfirmSchema,
  tailDeclareSchema,
  tailWithdrawSchema,
  softTissueDeclareSchema,
  softTissueWithdrawSchema,
  hairConfirmSchema,
  hairSetGravitySchema,
  hairSetMotionSchema,
  hairWithdrawSchema,
  packageSetSchema,
]);
export type Command = z.infer<typeof commandSchema>;

/**
 * The envelope gate (§3.1). An unknown verb refuses NAMING THE VOCABULARY —
 * a typo'd verb and a forbidden one are the same refusal, never a silent
 * skip (§8). Malformed params refuse with path-precise reasons, the
 * `parse*` idiom.
 */
export function parseCommand(raw: unknown): Command {
  if (typeof raw === 'object' && raw !== null && 'verb' in raw) {
    const verb = (raw as { verb: unknown }).verb;
    if (typeof verb !== 'string' || !(COMMAND_VERBS as readonly string[]).includes(verb)) {
      throw new CommandRefusal(
        `no verb ${JSON.stringify(verb)} — the log speaks: ${COMMAND_VERBS.join(', ')} (ADR-007 §3.1).`,
      );
    }
  }
  const parsed = commandSchema.safeParse(raw);
  if (!parsed.success) {
    throw new SchemaValidationError(
      'Command',
      parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
    );
  }
  return parsed.data;
}

/** Resolve a clip by name or refuse naming what exists (§8). */
function findClip(doc: AnimatorDocument, name: string, verb: string): AuthoredClip {
  const clip = doc.clips.find((c) => c.name === name);
  if (clip === undefined) {
    const live = doc.clips.map((c) => c.name).join(', ');
    throw new CommandRefusal(`${verb}: no clip "${name}" — clips: ${live || '(none)'} (§8).`);
  }
  return clip;
}

/**
 * Resolve a key by id on a bone's track or refuse LISTING THE LIVE IDENTITIES
 * — the §8 example, verbatim ("a key.delete on a dead id: the refusal lists
 * the live identities"). A missing track and a dead id are the same miss.
 */
function findTrackKey(clip: AuthoredClip, bone: string, id: string, verb: string): KeyTrack {
  const track = clip.tracks.find((t) => t.bone === bone);
  if (track === undefined || !track.keys.some((k) => k.id === id)) {
    const live = track?.keys.map((k) => k.id).join(', ') ?? '';
    throw new CommandRefusal(
      `${verb} "${id}" on "${bone}" in clip "${clip.name}": no such key — live keys: ${live || '(none)'} (§8).`,
    );
  }
  return track;
}

/** The next raw Document with one clip's tracks replaced. */
function withTracks(doc: AnimatorDocument, clipName: string, tracks: readonly KeyTrack[]): unknown {
  return { ...doc, clips: doc.clips.map((c) => (c.name === clipName ? { ...c, tracks } : c)) };
}

/** `Tail7_graft` (the session name) or `Tail7` (the exported one) → 7; anything else → null. */
function tailSlotOf(bone: string): number | null {
  const m = /^Tail(\d+)(?:_graft)?$/.exec(bone);
  return m === null ? null : Number(m[1]);
}

/**
 * A tail that shrinks (or goes) must not strand an edit on a slot it drops:
 * a pose or a key naming `Tail7_graft` would refuse at the next load as a
 * phantom bone. Refused HERE, naming the bones, so the person clears them
 * first — never a silent drop of their work.
 */
function refuseStrandedTailEdits(doc: AnimatorDocument, keep: number, verb: string): void {
  const stranded = new Set<string>();
  for (const p of doc.edits.poses) {
    const k = tailSlotOf(p.bone);
    if (k !== null && k > keep) stranded.add(p.bone);
  }
  for (const clip of doc.clips) {
    for (const t of clip.tracks) {
      const k = tailSlotOf(t.bone);
      if (k !== null && k > keep && t.keys.length > 0) stranded.add(`${t.bone} (keys in "${clip.name}")`);
    }
  }
  if (stranded.size > 0) {
    throw new CommandRefusal(
      `${verb} would drop tail bones that carry your edits: ${[...stranded].join(', ')} — clear those poses and keys first (§8).`,
    );
  }
}

/**
 * The one edit door (§2.1). Interprets a command against a Document and
 * returns a NEW parsed (frozen) Document; the input is untouched. Identity
 * misses refuse naming what exists (§8 — the take-by-name idiom).
 */
export function applyCommand(doc: AnimatorDocument, raw: unknown): AnimatorDocument {
  const command = parseCommand(raw);
  switch (command.verb) {
    case 'elbow.pin':
      return parseDocument({ ...doc, target: { ...doc.target, recenters: [
        ...(doc.target.recenters ?? []).filter(r => r.bone !== command.params.bone), command.params,
      ] } });
    case 'elbow.reset':
      return parseDocument({ ...doc, target: { ...doc.target, recenters:
        (doc.target.recenters ?? []).filter(r => r.bone !== command.params.bone || !r.rebuildHinge),
      } });
    case 'pose.set': {
      const { bone, rotation } = command.params;
      const poses = [...doc.edits.poses.filter((p) => p.bone !== bone), { bone, rotation }];
      return parseDocument({ ...doc, edits: { ...doc.edits, poses } });
    }
    case 'pose.clear': {
      const { bone } = command.params;
      if (!doc.edits.poses.some((p) => p.bone === bone)) {
        const posed = doc.edits.poses.map((p) => p.bone).join(', ');
        throw new CommandRefusal(
          `pose.clear "${bone}": no pose value on that bone — posed bones: ${posed || '(none)'} (§8).`,
        );
      }
      return parseDocument({
        ...doc,
        edits: { ...doc.edits, poses: doc.edits.poses.filter((p) => p.bone !== bone) },
      });
    }
    case 'clip.create': {
      const { name } = command.params;
      if (doc.clips.some((c) => c.name === name)) {
        throw new CommandRefusal(`clip.create "${name}": a clip by that name exists — clip names are identities (§8).`);
      }
      return parseDocument({ ...doc, clips: [...doc.clips, { name, tracks: [] }] });
    }
    case 'clip.materialize': {
      const { name, tracks: incoming } = command.params;
      if (doc.clips.some((c) => c.name === name)) {
        throw new CommandRefusal(
          `clip.materialize "${name}": a clip by that name exists — clip names are identities (§8). ` +
            `Materializing again would need a new name or a delete first.`,
        );
      }
      // One bone, one track. MEASURED: parseDocument already refuses both this
      // and the time collision below — the §2.1 re-check is the door that
      // actually holds them, and disabling either guard still throws. These two
      // exist for the MESSAGE: a §8 refusal naming the verb, the clip and the
      // bone, instead of a schema dump the caller has to decode. Do not read
      // them as the enforcement; the gate underneath is.
      const seen = new Set<string>();
      for (const t of incoming) {
        if (seen.has(t.bone)) {
          throw new CommandRefusal(
            `clip.materialize "${name}": bone "${t.bone}" appears in two tracks — one bone holds one track (§8).`,
          );
        }
        seen.add(t.bone);
      }
      const tracks: KeyTrack[] = incoming.map((t) => {
        const ordered = [...t.keys].sort((a, b) => a.time - b.time);
        // The same time-identity law key.set and key.retime carry: a time holds
        // one value at PLAYBACK precision (Float32). Diagnostic, per the note
        // above — the Document gate refuses the collision regardless.
        for (let i = 1; i < ordered.length; i += 1) {
          const prev = ordered[i - 1] as (typeof ordered)[number];
          const cur = ordered[i] as (typeof ordered)[number];
          if (Math.fround(prev.time) === Math.fround(cur.time)) {
            throw new CommandRefusal(
              `clip.materialize "${name}": bone "${t.bone}" carries two keys at ${cur.time}s ` +
                `(indistinguishable from ${prev.time}s at playback precision) — a time holds one value (§8).`,
            );
          }
        }
        return {
          bone: t.bone,
          nextKey: ordered.length + 1,
          keys: ordered.map((k, i) => ({ id: `k${i + 1}`, time: k.time, rotation: k.rotation })),
        };
      });
      return parseDocument({ ...doc, clips: [...doc.clips, { name, tracks }] });
    }
    case 'key.set': {
      const { clip: clipName, bone, time, rotation } = command.params;
      const clip = findClip(doc, clipName, 'key.set');
      // First key on a bone creates its track; the track then persists for the
      // lineage (an empty keys array keeps the counter — ids are never reused).
      const track = clip.tracks.find((t) => t.bone === bone) ?? { bone, nextKey: 1, keys: [] };
      // Time identity is PLAYBACK precision (Float32): two float64 times the
      // mixer cannot tell apart are the same key, so this is a value replace,
      // never a doomed near-duplicate the projection refuses later (Codex P2,
      // #61 round 2). The stored time stays the ORIGINAL key's — same identity,
      // same instant, new value.
      const atTime = track.keys.find((k) => Math.fround(k.time) === Math.fround(time));
      const nextTrack: KeyTrack =
        atTime !== undefined
          ? // A key already owns this time: its VALUE changes, its id does not
            // (§12.2 — identity survives the edit; this is autokey's re-key).
            { ...track, keys: track.keys.map((k) => (k.id === atTime.id ? { ...k, rotation } : k)) }
          : {
              ...track,
              nextKey: track.nextKey + 1,
              keys: [...track.keys, { id: `k${track.nextKey}`, time, rotation }].sort((a, b) => a.time - b.time),
            };
      const tracks = clip.tracks.some((t) => t.bone === bone)
        ? clip.tracks.map((t) => (t.bone === bone ? nextTrack : t))
        : [...clip.tracks, nextTrack];
      return parseDocument(withTracks(doc, clipName, tracks));
    }
    case 'key.delete': {
      const { clip: clipName, bone, id } = command.params;
      const clip = findClip(doc, clipName, 'key.delete');
      const track = findTrackKey(clip, bone, id, 'key.delete');
      // The counter does NOT decrement: the dead id stays dead (§12.2).
      const nextTrack = { ...track, keys: track.keys.filter((k) => k.id !== id) };
      return parseDocument(
        withTracks(
          doc,
          clipName,
          clip.tracks.map((t) => (t.bone === bone ? nextTrack : t)),
        ),
      );
    }
    case 'armSpace.set': {
      // An upsert like pose.set: the value replaces the value, undo restores
      // the prior Document wholesale (the substrate's property, never ours).
      return parseDocument({ ...doc, edits: { ...doc.edits, armSpace: command.params.degrees } });
    }
    case 'zone.create': {
      const { zone } = command.params;
      const zones = doc.zones ?? [];
      if (zones.some((z) => z.id === zone.id)) throw new CommandRefusal(`zone.create: id "${zone.id}" is already a zone (§8).`);
      if (zones.some((z) => z.name === zone.name)) {
        throw new CommandRefusal(`zone.create: a zone named "${zone.name}" already exists — a name is an identity (§8).`);
      }
      return parseDocument({ ...doc, zones: [...zones, zone] });
    }
    case 'zone.delete': {
      const zone = findZone(doc, command.params.id, 'zone.delete');
      return parseDocument({ ...doc, zones: (doc.zones ?? []).filter((z) => z.id !== zone.id) });
    }
    case 'zone.rename': {
      const { id, name } = command.params;
      findZone(doc, id, 'zone.rename');
      if ((doc.zones ?? []).some((z) => z.id !== id && z.name === name)) {
        throw new CommandRefusal(`zone.rename: a zone named "${name}" already exists (§8).`);
      }
      return parseDocument({ ...doc, zones: (doc.zones ?? []).map((z) => (z.id === id ? { ...z, name } : z)) });
    }
    case 'zone.setFaces': {
      const { id, faces, source, shape } = command.params;
      findZone(doc, id, 'zone.setFaces');
      return parseDocument({
        ...doc,
        zones: (doc.zones ?? []).map((z) => (z.id === id ? { ...z, faces, sources: [...z.sources, source], ...(shape !== undefined ? { shape } : {}) } : z)),
      });
    }
    case 'zone.confirm': {
      const { id, intent, confirmedBy } = command.params;
      findZone(doc, id, 'zone.confirm');
      return parseDocument({
        ...doc,
        zones: (doc.zones ?? []).map((z) =>
          z.id === id
            ? { ...z, intent, confirmedBy, sources: [...z.sources, { kind: 'human-edit' as const, detail: `confirmed:${intent}` }] }
            : z,
        ),
      });
    }
    case 'tail.declare': {
      const next = command.params;
      const before = (doc.target.tails ?? []).find((t) => t.zoneId === next.zoneId);
      if (before !== undefined && next.count.value < before.count.value) {
        refuseStrandedTailEdits(doc, next.count.value, `tail.declare: ${next.count.value} bones`);
      }
      const tails = [...(doc.target.tails ?? []).filter((t) => t.zoneId !== next.zoneId), next];
      return parseDocument({ ...doc, target: { ...doc.target, tails } });
    }
    case 'tail.withdraw': {
      const { zoneId } = command.params;
      if (!(doc.target.tails ?? []).some((t) => t.zoneId === zoneId)) {
        throw new CommandRefusal(`tail.withdraw: zone "${zoneId}" carries no tail (§8).`);
      }
      refuseStrandedTailEdits(doc, 0, 'tail.withdraw');
      return parseDocument({ ...doc, target: { ...doc.target, tails: (doc.target.tails ?? []).filter((t) => t.zoneId !== zoneId) } });
    }
    case 'softTissue.declare': {
      const next = command.params;
      const softTissue = [...(doc.target.softTissue ?? []).filter((s) => s.region !== next.region), next];
      return parseDocument({ ...doc, target: { ...doc.target, softTissue } });
    }
    case 'softTissue.withdraw': {
      const { region } = command.params;
      if (!(doc.target.softTissue ?? []).some((s) => s.region === region)) {
        throw new CommandRefusal(`softTissue.withdraw: ${region} carries no leaf (§8).`);
      }
      return parseDocument({ ...doc, target: { ...doc.target, softTissue: (doc.target.softTissue ?? []).filter((s) => s.region !== region) } });
    }
    case 'hair.confirm': {
      const { zone, hair } = command.params;
      if (zone.intent !== 'hair' || zone.confirmedBy === undefined) {
        throw new CommandRefusal(`hair.confirm: zone "${zone.name}" must be marked hair by a person (intent "hair", confirmedBy) (§8).`);
      }
      if (hair.zoneId !== zone.id) throw new CommandRefusal(`hair.confirm: the hair names zone "${hair.zoneId}" but carries "${zone.id}" (§8).`);
      const previous = doc.target.hair?.zoneId;
      const others = (doc.zones ?? []).filter((z) => z.id !== previous);
      if (others.some((z) => z.id === zone.id)) throw new CommandRefusal(`hair.confirm: id "${zone.id}" is already a zone (§8).`);
      if (others.some((z) => z.name === zone.name)) {
        throw new CommandRefusal(`hair.confirm: a zone named "${zone.name}" already exists — a name is an identity (§8).`);
      }
      return parseDocument({ ...doc, zones: [...others, zone], target: { ...doc.target, hair } });
    }
    case 'hair.setGravity': {
      const hair = doc.target.hair;
      if (hair === undefined) throw new CommandRefusal('hair.setGravity: no hair is confirmed on this body (§8).');
      return parseDocument({ ...doc, target: { ...doc.target, hair: { ...hair, gravity: command.params.gravity } } });
    }
    case 'hair.setMotion': {
      const hair = doc.target.hair;
      if (hair === undefined) throw new CommandRefusal('hair.setMotion: no hair is confirmed on this body (§8).');
      return parseDocument({ ...doc, target: { ...doc.target, hair: { ...hair, motion: command.params.motion } } });
    }
    case 'hair.withdraw': {
      const hair = doc.target.hair;
      if (hair === undefined) throw new CommandRefusal('hair.withdraw: no hair is confirmed on this body (§8).');
      const target = { ...doc.target };
      delete target.hair;
      return parseDocument({ ...doc, zones: (doc.zones ?? []).filter((z) => z.id !== hair.zoneId), target });
    }
    case 'package.set': {
      // The same upsert; the choices replace the choices, undo restores the
      // prior Document wholesale. Readiness is NOT judged here — a role
      // without its archetype is a decision in progress, not a lie.
      return parseDocument({ ...doc, package: command.params.package });
    }
    case 'tailSpring.set': {
      // The same upsert; undo past the first set restores the ABSENT field —
      // the tail's source decides again (the prior, or the file's declaration).
      return parseDocument({ ...doc, edits: { ...doc.edits, tailSpring: command.params.settings } });
    }
    case 'headSpace.set': {
      // Same upsert. Undo past the FIRST set restores an ABSENT field — which
      // by the edits contract means the measured seating baseline, not zero
      // (or, on a file that DECLARES a hand-set seating, that declaration —
      // ADR-030 §14). The value is the character's TOTAL from its authored
      // rest, whatever bind the file arrived with.
      return parseDocument({ ...doc, edits: { ...doc.edits, headSpace: command.params.degrees } });
    }
    case 'material.setMapUse': {
      // The same upsert as setFactor: one channel on one material, every other
      // value untouched. Undo past the first set restores an ABSENT field,
      // which by the edits contract means the map is attached — the file's own
      // behaviour, never a stored "true" pretending to be an edit.
      const { material, channel, fromMap } = command.params;
      const key = channel === 'metallic' ? 'metallicFromMap' : 'roughnessFromMap';
      const existing = doc.edits.materials ?? [];
      const materials = existing.some((m) => m.material === material)
        ? existing.map((m) => (m.material === material ? { ...m, [key]: fromMap } : m))
        : [...existing, { material, [key]: fromMap }];
      return parseDocument({ ...doc, edits: { ...doc.edits, materials } });
    }
    case 'material.setFactor':
    case 'material.setColor': {
      // Upsert ONE channel on ONE material; every other channel's value (and
      // every other material's entry) survives untouched. Undo past the first
      // set restores an absent entry — the material wears what the file
      // shipped, by the edits contract.
      const { material, channel, value } = command.params;
      const key = (
        {
          metallic: 'metallicFactor',
          roughness: 'roughnessFactor',
          normalScale: 'normalScale',
          occlusionStrength: 'occlusionStrength',
          baseColor: 'baseColorFactor',
          emissive: 'emissiveFactor',
        } as const
      )[channel];
      const existing = doc.edits.materials ?? [];
      const materials = existing.some((m) => m.material === material)
        ? existing.map((m) => (m.material === material ? { ...m, [key]: value } : m))
        : [...existing, { material, [key]: value }];
      return parseDocument({ ...doc, edits: { ...doc.edits, materials } });
    }
    case 'key.retime': {
      const { clip: clipName, bone, id, time } = command.params;
      const clip = findClip(doc, clipName, 'key.retime');
      const track = findTrackKey(clip, bone, id, 'key.retime');
      // Collision at PLAYBACK precision (Float32) — the same identity rule as
      // key.set: a destination the mixer cannot distinguish from another key's
      // time is that key's time (Codex P2, #61 round 2).
      const collision = track.keys.find((k) => k.id !== id && Math.fround(k.time) === Math.fround(time));
      if (collision !== undefined) {
        throw new CommandRefusal(
          `key.retime "${id}" to ${time}s collides with key "${collision.id}" on "${bone}" — ` +
            `a time holds one value at playback precision (§8).`,
        );
      }
      const nextTrack = {
        ...track,
        keys: track.keys.map((k) => (k.id === id ? { ...k, time } : k)).sort((a, b) => a.time - b.time),
      };
      return parseDocument(
        withTracks(
          doc,
          clipName,
          clip.tracks.map((t) => (t.bone === bone ? nextTrack : t)),
        ),
      );
    }
  }
}

/**
 * Every rig bone a command ADDRESSES — the list a surface validates against the
 * live rig before committing, so a phantom-bone edit refuses atomically instead
 * of entering History (§8; the store's §54 P1 refusal).
 *
 * Exhaustive on purpose. The check it serves was written as
 * `'bone' in params ? params.bone : null`, which reads exactly one bone: the
 * day a verb arrived carrying a LIST of them (`clip.materialize`, sixty tracks
 * at a time), every one of those bones bypassed the gate silently. A law
 * enforced at one door is not enforced. Now the vocabulary declares its own
 * targets and a new verb cannot compile without saying what it addresses.
 */
export function commandTargetBones(command: Command): readonly string[] {
  switch (command.verb) {
    case 'elbow.pin':
    case 'elbow.reset':
    case 'pose.set':
    case 'pose.clear':
      return [command.params.bone];
    case 'clip.create':
      return [];
    /**
     * armSpace.set names no bone: WHICH bones carry the offset is derived from
     * the live rig's mapping at display time (armSpaceOffsets), and a rig that
     * cannot resolve both arms refuses there — the panel's disabled evidence,
     * not this gate's concern (ADR-017 §6).
     */
    case 'armSpace.set':
    case 'headSpace.set':
      return [];
    /** tailSpring.set names no bone: the chain is whatever the run grew or the file declared (ADR-038). */
    case 'tailSpring.set':
      return [];
    /**
     * material.* names a MATERIAL, not a bone — whether the live character
     * carries it is the surface's apply-time question (ADR-020), exactly as
     * armSpace resolves its bones at display time.
     */
    case 'material.setFactor':
    case 'material.setMapUse':
    case 'material.setColor':
      return [];
    /** zone.* names FACES, not bones (ADR-031): the chain a tail zone will own is T2's graft, a new Document. */
    case 'zone.create':
    case 'zone.delete':
    case 'zone.rename':
    case 'zone.setFaces':
    case 'zone.confirm':
      return [];
    /**
     * tail.* names a ZONE: the bones it grows do not exist until the run
     * grafts them, so there is nothing on the live rig to validate — the run
     * refuses by name if the zone cannot carry them (ADR-031 §8).
     */
    case 'tail.declare':
    case 'tail.withdraw':
      return [];
    /** softTissue.* names a REGION: its leaf does not exist until the run grafts it (ADR-038 J2). */
    case 'softTissue.declare':
    case 'softTissue.withdraw':
      return [];
    /** hair.* names a REGION and a skull; it grows no bone in T1 (ADR-039). */
    case 'hair.confirm':
    case 'hair.setGravity':
    case 'hair.setMotion':
    case 'hair.withdraw':
      return [];
    /** package.set names roles, facets and clips — no bone (ADR-033). */
    case 'package.set':
      return [];
    case 'clip.materialize':
      return command.params.tracks.map((t) => t.bone);
    case 'key.set':
    case 'key.delete':
    case 'key.retime':
      return [command.params.bone];
  }
}

/**
 * The human-readable narrative line (§3.3): what the surface announces on
 * undo/redo. Derived from the command's own data so it can never drift from
 * the params — the one field ADR-119's interface got unambiguously right,
 * kept without letting authors hand-write a lie into it.
 */
export function describeCommand(command: Command): string {
  switch (command.verb) {
    case 'elbow.pin': return `pinned elbow ${command.params.bone}`;
    case 'elbow.reset': return `restored imported elbow ${command.params.bone}`;
    case 'pose.set':
      return `posed "${command.params.bone}"`;
    case 'pose.clear':
      return `cleared the pose on "${command.params.bone}"`;
    case 'clip.create':
      return `created clip "${command.params.name}"`;
    case 'clip.materialize': {
      const keys = command.params.tracks.reduce((n, t) => n + t.keys.length, 0);
      return (
        `materialized clip "${command.params.name}" — ` +
        `${keys} keys across ${command.params.tracks.length} bones`
      );
    }
    case 'key.set':
      return `keyed "${command.params.bone}" at ${command.params.time}s in "${command.params.clip}"`;
    case 'key.delete':
      return `deleted key ${command.params.id} on "${command.params.bone}" in "${command.params.clip}"`;
    case 'key.retime':
      return `retimed key ${command.params.id} on "${command.params.bone}" to ${command.params.time}s`;
    case 'armSpace.set':
      return command.params.degrees === 0
        ? 'reset arm-space to neutral'
        : `set arm-space to ${command.params.degrees}°`;
    // Zero is NOT neutral here — it explicitly overrides the measured seating
    // baseline with "bone-faithful, no correction" — so it names its degrees.
    case 'headSpace.set':
      return `set head-space to ${command.params.degrees}°`;
    case 'tailSpring.set': {
      const t = command.params.settings;
      return t.on
        ? `set the tail spring — ${t.mood}, muscle ${t.muscle.toFixed(2)}, stiffness ${t.stiffness.toFixed(2)}, drag ${t.drag.toFixed(2)}, gravity ${t.gravity.toFixed(3)}`
        : `switched the tail spring off (${t.mood}, driven without physics)`;
    }
    case 'material.setFactor': {
      const channelNames = {
        metallic: 'metallic',
        roughness: 'roughness',
        normalScale: 'normal scale',
        occlusionStrength: 'occlusion strength',
      } as const;
      return `set ${channelNames[command.params.channel]} factor on "${command.params.material}" to ${command.params.value}`;
    }
    case 'material.setMapUse':
      return (
        `${command.params.fromMap ? 'took' : 'stopped taking'} ${command.params.channel} from the map ` +
        `on "${command.params.material}"`
      );
    // The narrative speaks sRGB hex — what a person saw in the picker — while
    // the Document keeps linear (the file's truth-space).
    case 'material.setColor': {
      const hex = command.params.value
        .map((c) => {
          const s = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
          return Math.round(s * 255)
            .toString(16)
            .padStart(2, '0');
        })
        .join('');
      const name = command.params.channel === 'baseColor' ? 'base color' : 'emissive';
      return `set ${name} on "${command.params.material}" to #${hex}`;
    }
    case 'zone.create':
      return `proposed zone "${command.params.zone.name}" (${command.params.zone.faces.length} faces)`;
    case 'zone.delete':
      return `deleted zone ${command.params.id}`;
    case 'zone.rename':
      return `renamed zone ${command.params.id} to "${command.params.name}"`;
    case 'zone.setFaces':
      return `reshaped zone ${command.params.id} to ${command.params.faces.length} faces (${command.params.source.detail})`;
    case 'zone.confirm':
      return `confirmed zone ${command.params.id} is ${command.params.intent === 'tail' ? 'a tail' : command.params.intent === 'softTissue' ? 'soft tissue' : command.params.intent === 'hair' ? 'hair' : 'cloth'}`;
    case 'tail.declare':
      return `grew a tail of ${command.params.count.value} bones on zone ${command.params.zoneId}`;
    case 'tail.withdraw':
      return `withdrew the tail on zone ${command.params.zoneId}`;
    case 'softTissue.declare':
      return `grew a ${command.params.region} bounce leaf${command.params.zoneId !== undefined ? ` on zone ${command.params.zoneId}` : ''}`;
    case 'softTissue.withdraw':
      return `withdrew the ${command.params.region} bounce leaf`;
    case 'hair.confirm':
      return `confirmed hair (${command.params.hair.treatment}, ${command.params.zone.faces.length} faces, skull ${command.params.hair.skull.provenance}) — gravity ${command.params.hair.gravity.toFixed(2)}`;
    case 'hair.setGravity':
      return command.params.gravity === 0 ? 'hair holds the sculpt (gravity 0)' : `set hair gravity to ${command.params.gravity.toFixed(2)}`;
    case 'hair.setMotion': {
      const m = command.params.motion;
      return `set the hair's motion: bounce ${m.bounce.toFixed(1)}, sway ${m.sway.toFixed(1)}, jiggle ${m.jiggle.toFixed(1)}`;
    }
    case 'hair.withdraw':
      return 'withdrew the hair — not hair';
    case 'package.set': {
      const p = command.params.package;
      return `set the World package — ${p.roles.join(' + ')} · ${p.facets.body}, ${p.facets.presentation} · ${p.heightMeters.value} m`;
    }
  }
}

/** The zone, or a refusal that names the ids that exist (§8). */
function findZone(doc: AnimatorDocument, id: string, verb: string): Zone {
  const zone = (doc.zones ?? []).find((z) => z.id === id);
  if (zone === undefined) {
    const known = (doc.zones ?? []).map((z) => `${z.id} "${z.name}"`).join(', ');
    throw new CommandRefusal(`${verb} "${id}": no such zone — zones: ${known || '(none)'} (§8).`);
  }
  return zone;
}
